/**
 * Organisation details in a side panel (5 Oct 2026): opened from the avatar menu, slides in
 * from the right and takes 90% of the screen — the same form as /me/organisation, so it reads
 * like the Attributes page rather than squeezed into Clerk's account popup.
 */

import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Maximize2, X } from 'lucide-react'
import { T } from '@/components/shell/orgTheme'
import { MyOrgDetails } from './MyOrgDetails'

/** Defaults to the signed-in member's own details. Members → Edit details passes another
 *  member's form as `children`, with their name as `title` (and no page to open). */
export function OrgDetailsPanel({ onClose, title = 'Organisation details', children, pagePath = '/me/organisation' }: {
  onClose: () => void; title?: string; children?: ReactNode; pagePath?: string | null
}) {
  const navigate = useNavigate()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev }
  }, [onClose])

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={title} style={{ position: 'fixed', inset: 0, zIndex: 100 }}>
      <div onClick={onClose} className="lb-fade-in" style={{ position: 'absolute', inset: 0, background: 'rgba(20,16,30,.32)' }} />
      <aside className="lb-slide-in" style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '90%', background: T.page, boxShadow: '-20px 0 50px rgba(20,16,30,.18)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '18px 28px', background: '#fff', borderBottom: `1px solid ${T.border}` }}>
          <h1 style={{ flex: 1, minWidth: 0, margin: 0, fontSize: 22, fontWeight: 600, color: T.dark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</h1>
          {pagePath && (
            <button onClick={() => { onClose(); navigate(pagePath) }} aria-label="Open as a page" title="Open as a page"
              className="hover:bg-[#F4F2F6]" style={iconBtn}><Maximize2 size={17} /></button>
          )}
          <button onClick={onClose} aria-label="Close" title="Close" className="hover:bg-[#F4F2F6]" style={iconBtn}><X size={20} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px 48px' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>{children ?? <MyOrgDetails />}</div>
        </div>
      </aside>
    </div>,
    document.body,
  )
}

const iconBtn = { width: 36, height: 36, display: 'grid', placeItems: 'center', border: 'none', borderRadius: 8, background: 'transparent', color: T.ink, cursor: 'pointer' } as const
